import React, { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Camera, Lightbulb, Map as MapIcon, Route as RouteIcon, Star } from "lucide-react";
import { TspCard, TspDonutChart, TspProgressBar, TspTable } from "../../../components/primitives";
import { RESPONSE_RATINGS } from "../../../constants/tspOperations";
import { formatDate, formatNumber } from "../../../utils/tspFormat";
import AdviceCategoryBadge from "../../advice-repository/components/AdviceCategoryBadge";
import { leadSection, summarize } from "../../advice-repository/adviceModel";
import MediaThumb from "../../media-gallery/components/MediaThumb";
import { barPercent, methodChartData, methodLabel, paletteColor, responseLabel, teamsOf } from "../knowledgeSelectors";

// The nine sections of the city knowledge view. Each takes the city as the API
// returns it (`city.location`, `city.stats`, `city.sections`) and `perms`, the
// caller's TSP flags. A section the caller may not see arrives as `null` and
// says so; an empty one says nothing has been recorded — never the same thing.

// Prefixes every section's DOM id (`id` / heading id). Empty on the Knowledge
// Repository page; set when the sections are shown inside another page (a
// journey's "View Summary"), whose own section anchors ("overview", ...) must
// keep their ids.
const SectionIdPrefix = createContext("");

function Section({ id: sectionId, title, action, children }) {
  const id = `${useContext(SectionIdPrefix)}${sectionId}`;
  return (
    <section id={id} aria-labelledby={`${id}-title`}>
      <TspCard title={<span id={`${id}-title`}>{title}</span>} action={action} className="tsp-title-18">
        {children}
      </TspCard>
    </section>
  );
}

const SectionEmpty = ({ children }) => <p className="tsp-muted-text tsp-section-empty">{children}</p>;
const Restricted = ({ what }) => <SectionEmpty>You do not have access to {what} for this city.</SectionEmpty>;
const dateOnly = (value) => formatDate(String(value).slice(0, 10));
const yesNo = (value) => (value === true ? "Yes" : value === false ? "No" : "—");

// ---------- 1. Overview ----------
export function OverviewSection({ city, perms }) {
  const { stats, location, sections } = city;
  const tiles = [
    { label: "Visits", value: stats.visit_count, icon: RouteIcon, tone: "orange" },
    { label: "Journeys completed", value: stats.journeys_completed, icon: MapIcon, tone: "blue" },
    stats.total_books !== null && { label: "Books distributed", value: formatNumber(stats.total_books), icon: BookOpen, tone: "orange" },
    sections.public_response && { label: "Top response", value: responseLabel(sections.public_response.top_rating), icon: Star, tone: "green" },
    stats.photos !== null && { label: "Media items", value: stats.photos + stats.videos, icon: Camera, tone: "blue" },
    stats.advice !== null && { label: "Advice", value: stats.advice, icon: Lightbulb, tone: "green" },
  ].filter(Boolean);
  const teams = teamsOf(sections.visits?.items);

  return (
    <Section id="overview" title="Overview">
      <div className="tsp-knowledge-stats">
        {tiles.map((tile) => (
          <div key={tile.label} className="tsp-year-stat-tile">
            <tile.icon size={16} className={`tsp-year-stat-icon tsp-icon-${tile.tone}`} aria-hidden="true" />
            <div className="tsp-year-stat-value">{tile.value}</div>
            <div className="tsp-year-stat-label">{tile.label}</div>
          </div>
        ))}
      </div>

      <dl className="tsp-summary-list tsp-knowledge-facts">
        <div>
          <dt>Last visited</dt>
          <dd>{dateOnly(stats.last_visited)}</dd>
        </div>
        {sections.distribution && (
          <div>
            <dt>Best distribution method</dt>
            <dd>{methodLabel(sections.distribution.best_method)}</dd>
          </div>
        )}
        {sections.visits && (
          <div>
            <dt>Teams that visited</dt>
            <dd>{teams.length > 0 ? teams.join(", ") : "Recorded by other teams"}</dd>
          </div>
        )}
      </dl>
    </Section>
  );
}

// ---------- 2. Visit history ----------
export function VisitHistorySection({ city }) {
  const visits = city.sections.visits;
  if (!visits) {
    return (
      <Section id="visit-history" title="Visit History">
        <Restricted what="visit history" />
      </Section>
    );
  }
  const showBooks = visits.items.some((visit) => visit.books !== null);
  const showResponse = visits.items.some((visit) => visit.responses !== null);
  const columns = [
    {
      key: "journey",
      label: "Journey",
      render: (row) =>
        row.journey ? (
          <Link to={`/tsp/journeys/${row.journey.id}`} className="tsp-table-link">
            {row.journey.name}
          </Link>
        ) : (
          <span className="tsp-cell-muted">Another team&apos;s journey</span>
        ),
    },
    { key: "team", label: "Team", render: (row) => <span className="tsp-cell-muted">{row.team ? row.team.name : "—"}</span> },
    { key: "date", label: "Visited", render: (row) => dateOnly(row.visit_date) },
    ...(showBooks ? [{ key: "books", label: "Books", render: (row) => <strong className="tsp-cell-accent">{formatNumber(row.books)}</strong> }] : []),
    ...(showResponse ? [{ key: "response", label: "Response", render: (row) => <strong className="tsp-cell-positive">{row.responses === 0 ? "No responses" : responseLabel(row.top_response)}</strong> }] : []),
  ];
  return (
    <Section id="visit-history" title="Visit History" action={<span className="tsp-muted-text">{visits.total} {visits.total === 1 ? "visit" : "visits"}</span>}>
      <TspTable columns={columns} rows={visits.items} rowKey="id" className="tsp-table-boxed" />
      {visits.total > visits.items.length && <p className="tsp-muted-text">Showing the {visits.items.length} most recent of {visits.total} visits.</p>}
    </Section>
  );
}

// ---------- 3–5. Contacts, accommodation, prasadam ----------
function RecordSection({ id, title, data, what, emptyText, linkTo, linkLabel, columns }) {
  return (
    <Section id={id} title={title} action={linkTo && <Link to={linkTo} className="tsp-view-all-link">{linkLabel}</Link>}>
      {data === null ? <Restricted what={what} /> : data.items.length === 0 ? <SectionEmpty>{emptyText}</SectionEmpty> : <TspTable columns={columns} rows={data.items} rowKey="id" className="tsp-table-boxed" />}
      {data && data.total > data.items.length && <p className="tsp-muted-text">Showing {data.items.length} of {data.total}.</p>}
    </Section>
  );
}

const dash = (value) => (value === null || value === undefined || value === "" ? "—" : value);
const money = (value) => (value === null || value === undefined ? "Free" : `₹${value}`);

export function ContactsSection({ city, perms }) {
  return (
    <RecordSection
      id="contacts"
      title="Contacts"
      what="contacts"
      data={city.sections.contacts}
      emptyText="No contact records available."
      linkTo={perms.viewContacts ? "/tsp/contacts" : null}
      linkLabel="Contact Directory"
      columns={[
        { key: "name", label: "Name", render: (row) => <strong className="tsp-cell-strong">{row.name}</strong> },
        { key: "phone", label: "Phone", render: (row) => dash(row.phone) },
        { key: "category", label: "Category", render: (row) => dash(row.category) },
        { key: "remarks", label: "Notes", render: (row) => <span className="tsp-cell-muted">{dash(row.remarks)}</span> },
      ]}
    />
  );
}

export function AccommodationSection({ city, perms }) {
  return (
    <RecordSection
      id="accommodation"
      title="Accommodation"
      what="accommodation"
      data={city.sections.accommodation}
      emptyText="No accommodation records available."
      linkTo={perms.viewAccommodation ? "/tsp/accommodation" : null}
      linkLabel="All accommodation"
      columns={[
        { key: "stay_name", label: "Stay", render: (row) => <strong className="tsp-cell-strong">{row.stay_name}</strong> },
        { key: "address", label: "Address", render: (row) => <span className="tsp-cell-muted">{dash(row.address)}</span> },
        { key: "charges", label: "Charges", render: (row) => money(row.charges) },
        { key: "capacity", label: "Capacity", render: (row) => (row.capacity == null ? "—" : `${row.capacity} persons`) },
        { key: "contact", label: "Contact", render: (row) => dash(row.contact) },
      ]}
    />
  );
}

export function PrasadamSection({ city, perms }) {
  return (
    <RecordSection
      id="prasadam"
      title="Prasadam"
      what="prasadam facilities"
      data={city.sections.prasadam}
      emptyText="No prasadam facility records available."
      linkTo={perms.viewPrasadam ? "/tsp/prasadam" : null}
      linkLabel="All prasadam facilities"
      columns={[
        { key: "facility_name", label: "Facility", render: (row) => <strong className="tsp-cell-strong">{row.facility_name}</strong> },
        { key: "has_water", label: "Water", render: (row) => yesNo(row.has_water) },
        { key: "has_electricity", label: "Electricity", render: (row) => yesNo(row.has_electricity) },
        { key: "cooking_capacity", label: "Cooking capacity", render: (row) => (row.cooking_capacity == null ? "—" : `${row.cooking_capacity} persons`) },
        { key: "remarks", label: "Remarks", render: (row) => <span className="tsp-cell-muted">{dash(row.remarks)}</span> },
      ]}
    />
  );
}

// ---------- 7. Book distribution ----------
export function DistributionSection({ city }) {
  const distribution = city.sections.distribution;
  if (!distribution) {
    return (
      <Section id="book-distribution" title="Book Distribution">
        <Restricted what="book distribution" />
      </Section>
    );
  }
  const { total_books: total, by_book: byBook, by_method: byMethod } = distribution;
  return (
    <Section id="book-distribution" title="Book Distribution" action={<span className="tsp-status-pill tsp-status-pill-danger">{formatNumber(total)} Total Books</span>}>
      {total === 0 ? (
        <SectionEmpty>No book distribution has been recorded for {city.location.city_name} yet.</SectionEmpty>
      ) : (
        <div className="tsp-stats-two-col">
          <div>
            <h4 className="tsp-subheading">By Book Type</h4>
            <div className="tsp-stats-panel tsp-bars-books">
              {byBook.map((entry, index) => (
                <TspProgressBar key={entry.name} label={entry.name} value={formatNumber(entry.quantity)} percent={barPercent(entry.quantity, byBook)} color={paletteColor(index)} />
              ))}
            </div>
          </div>
          <div>
            <h4 className="tsp-subheading">By Method</h4>
            <div className="tsp-stats-panel">
              <TspDonutChart data={methodChartData(byMethod)} centerLabel="Total Books" centerValue={formatNumber(total)} height={150} />
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------- 8. Public response ----------
export function ResponseSection({ city }) {
  const response = city.sections.public_response;
  if (!response) {
    return (
      <Section id="public-response" title="Public Response">
        <Restricted what="public response" />
      </Section>
    );
  }
  return (
    <Section id="public-response" title="Public Response" action={<span className="tsp-muted-text tsp-response-count">{response.total} responses</span>}>
      {response.total === 0 ? (
        <SectionEmpty>No public response has been recorded for {city.location.city_name} yet.</SectionEmpty>
      ) : (
        <div className="tsp-response-layout">
          <div className="tsp-response-score">
            <div className="tsp-response-breakdown">
              {RESPONSE_RATINGS.map((rating) => {
                const row = response.breakdown.find((entry) => entry.rating === rating.value);
                return <TspProgressBar key={rating.value} label={rating.label} value={`${row.percent}%`} percent={row.percent} color={rating.color} />;
              })}
            </div>
          </div>
          <div className="tsp-stats-panel tsp-response-facts">
            <dl className="tsp-summary-list">
              <div>
                <dt>Most common response</dt>
                <dd>{responseLabel(response.top_rating)}</dd>
              </div>
              <div>
                <dt>Total responses</dt>
                <dd>{formatNumber(response.total)}</dd>
              </div>
              {city.sections.distribution && (
                <div>
                  <dt>Best method</dt>
                  <dd>{methodLabel(city.sections.distribution.best_method)}</dd>
                </div>
              )}
              <div>
                <dt>Completed visits</dt>
                <dd>{city.stats.visit_count}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------- 9. Media ----------
export function MediaSection({ city, perms }) {
  const media = city.sections.media;
  const galleryLink = `/tsp/media-gallery?location_id=${city.location.id}`;
  return (
    <Section
      id="media"
      title="Media"
      action={
        media && perms.viewMedia && media.total > 0 ? (
          <Link to={galleryLink} className="tsp-view-all-link">
            View all {media.total} in Media Gallery
          </Link>
        ) : media ? (
          <span className="tsp-muted-text">{media.total} items</span>
        ) : null
      }
    >
      {media === null ? (
        <Restricted what="media" />
      ) : media.items.length === 0 ? (
        <SectionEmpty>No media available for this city.</SectionEmpty>
      ) : (
        <div className="tsp-knowledge-media-grid">
          {media.items.map((item) => {
            const tile = (
              <>
                <MediaThumb item={item} />
                <span className="tsp-media-tile-caption">{item.caption}</span>
              </>
            );
            return perms.viewMedia ? (
              <Link key={item.id} to={galleryLink} className="tsp-media-tile">
                {tile}
              </Link>
            ) : (
              <div key={item.id} className="tsp-media-tile">
                {tile}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ---------- 10. Team advice ----------
export function AdviceSection({ city, perms }) {
  const advice = city.sections.advice;
  return (
    <Section
      id="team-advice"
      title="Team Advice"
      action={
        advice && perms.viewAdvice && advice.total > 0 ? (
          <Link to={`/tsp/advice?location_id=${city.location.id}`} className="tsp-view-all-link">
            View all {advice.total}
          </Link>
        ) : null
      }
    >
      {advice === null ? (
        <Restricted what="advice" />
      ) : advice.items.length === 0 ? (
        <SectionEmpty>No advice has been recorded yet.</SectionEmpty>
      ) : (
        <ul className="tsp-advice-related">
          {advice.items.map((entry) => {
            const lead = leadSection(entry);
            return (
              <li key={entry.id}>
                <span className="tsp-card-action-group">
                  {entry.categories.map((key) => (
                    <AdviceCategoryBadge key={key} category={key} />
                  ))}
                </span>
                {perms.viewAdvice ? <Link to={`/tsp/advice/${entry.id}`}>{lead ? summarize(lead.text, 90) : "Advice"}</Link> : <span className="tsp-cell-strong">{lead ? summarize(lead.text, 90) : "Advice"}</span>}
                <span className="tsp-muted-text tsp-advice-related-meta">
                  {entry.submitted_by ? `${entry.submitted_by.name} · ` : ""}
                  {dateOnly(entry.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// All nine sections in page order — the Knowledge Repository city page, and a
// journey's inline "View Summary" (with `idPrefix`, see SectionIdPrefix).
export function CityKnowledgeSections({ city, perms, idPrefix = "" }) {
  return (
    <SectionIdPrefix.Provider value={idPrefix}>
      <OverviewSection city={city} perms={perms} />
      <VisitHistorySection city={city} />
      <ContactsSection city={city} perms={perms} />
      <AccommodationSection city={city} perms={perms} />
      <PrasadamSection city={city} perms={perms} />
      <DistributionSection city={city} />
      <ResponseSection city={city} />
      <MediaSection city={city} perms={perms} />
      <AdviceSection city={city} perms={perms} />
    </SectionIdPrefix.Provider>
  );
}
